import ItemJS from "itemsjs";

import type { SearchOptions } from "itemsjs";

let _instance: FacetedSearch<any> | null = null;

type Configuration<T extends {}> = ItemJS.Configuration<
  T,
  string,
  keyof T & string
>;

export class FacetedSearch<T extends { id: string | number }> {
  private _facet_service?: ReturnType<typeof ItemJS<T>> | null;
  private _raw_data: T[] = [];
  private _filters: SearchOptions<T, string, keyof T & string>["filters"] = {};
  private _search_results: T[] = [];
  private _aggregations: Record<
    string,
    ItemJS.SearchAggregation<T, keyof T & string>
  > = {};
  private _options: Configuration<T> = {};
  private _searchCallback?: () => void;

  public constructor() {
    if (!_instance) {
      _instance = this;
    }
    return _instance;
  }

  public setOptions<O extends keyof Partial<T> & string>(
    options: ItemJS.Configuration<T, string, O>
  ) {
    if (this.shouldUpdateOptions(options as Configuration<T>)) {
      this._options = options as Configuration<T>;
      this.initFacetService();
    }
    return this;
  }

  public setData(data: T[]) {
    if (this.shouldUpdateData(data)) {
      this._raw_data = data;
      this.initFacetService();
    }
    return this;
  }

  public setFilter(key: string, filter: any) {
    if (filter) {
      this.ensureKeyIsAnAggregation(key);
      this._filters![key] = filter;
    }
    return this;
  }

  public onSearch(callback: () => void) {
    this._searchCallback = callback;
    return this;
  }

  public search() {
    this.ensureDataIsSet();
    const search = this._facet_service?.search(this.getSearchOptions());
    this._search_results = search?.data.allFilteredItems || [];
    this._aggregations = (search?.data.aggregations as any) || {};

    return this;
  }

  public getSearchResult() {
    if (this._searchCallback) {
      this._searchCallback();
    }

    return this._search_results || [];
  }

  public getAggregation(key: string) {
    return this._aggregations?.[key];
  }

  public reset() {
    this._facet_service = null;
    this._filters = {};
    this._aggregations = {};
    this._options = {};
    this._search_results = [];
    this._raw_data = [];
    return this;
  }

  private initFacetService() {
    try {
      this.ensureDataIsSet();
      this._facet_service = ItemJS<T>(this._raw_data, this._options);
    } catch {}
  }

  private ensureKeyIsAnAggregation(
    key: string
  ): asserts key is keyof T & string {
    const aggregations = this._options.aggregations;

    if (
      !aggregations ||
      !(key in aggregations) ||
      !aggregations[key as keyof T & string]
    ) {
      throw new Error(
        `${key} is not an aggregated key. Make sure key2 was set in \`aggregations\` when calling \`.setOptions\``
      );
    }
  }

  private ensureDataIsSet() {
    if (this._raw_data.length < 1) {
      throw new Error(
        "No data has been set. Please make sure to call `.setData` first."
      );
    }
  }

  private shouldUpdateData(data: T[]): boolean {
    return (
      data.length !== this._raw_data?.length ||
      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      this._raw_data[0].id !== data[0].id
    );
  }

  private shouldUpdateOptions(options: Configuration<T>): boolean {
    return options !== this._options;
  }

  private getSearchOptions():
    | ItemJS.SearchOptions<T, string, keyof T & string>
    | undefined {
    return {
      filters: this._filters,
      is_all_filtered_items: true
    };
  }
}
